import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IStaffPreferences } from "../../../../@types/Preferences";
import { Checkbox } from "../../../../components/Checkbox";
import { TextField } from "../../../../components/TextField";
import { useToast } from "../../../../hooks/useToast";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { staffService } from "../../../../services/staffService";
import { isValidHttpUrl, MAX_URL_LENGTH } from "../../../../utils/url";
import {
  AppointmentUrlField,
  AppointmentUrlRow,
  MessagesPreferencesContainer,
  NotificationRow,
  ParentContainer,
  TextContainer,
  UrlLabel,
} from "./MessagesPreferencesSection.styles";

const SAVE_DEBOUNCE_MS = 800;

export function MessagesPreferencesSection() {
  const { t } = useTranslation();

  const { staffPreferences, changeStaffPreferences } = useUserPreferences();
  const { showToast } = useToast();

  const [appointmentUrlInput, setAppointmentUrlInput] = useState(
    staffPreferences.defaultAppointmentUrl ?? ""
  );
  const [appointmentUrlError, setAppointmentUrlError] = useState<
    string | undefined
  >();

  const [hcpAppointmentUrlInput, setHcpAppointmentUrlInput] = useState(
    staffPreferences.hcpAppointmentUrl ?? ""
  );
  const [hcpAppointmentUrlError, setHcpAppointmentUrlError] = useState<
    string | undefined
  >();

  useEffect(() => {
    const trimmed = appointmentUrlInput.trim();
    const savedUrl = (staffPreferences.defaultAppointmentUrl ?? "").trim();

    if (trimmed === savedUrl) {
      return undefined;
    }

    if (trimmed && !isValidHttpUrl(trimmed)) {
      setAppointmentUrlError(t("settings_messages_invalidUrl"));
      return undefined;
    }

    setAppointmentUrlError(undefined);

    const timeoutId = window.setTimeout(async () => {
      const valueToSend = trimmed === "" ? null : trimmed;

      try {
        const result = await staffService.updateDefaultAppointmentUrl(
          valueToSend
        );

        if (result.status >= 200 && result.status < 300) {
          changeStaffPreferences({
            ...staffPreferences,
            defaultAppointmentUrl: valueToSend ?? undefined,
          });
          showToast(t("settings_messages_defaultUrlSaved"), "success");
        } else {
          showToast(t("settings_messages_defaultUrlFailed"), "error");
        }
      } catch {
        showToast(t("settings_messages_defaultUrlFailed"), "error");
      }
    }, SAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    appointmentUrlInput,
    staffPreferences,
    changeStaffPreferences,
    showToast,
  ]);

  useEffect(() => {
    const trimmed = hcpAppointmentUrlInput.trim();
    const savedUrl = (staffPreferences.hcpAppointmentUrl ?? "").trim();

    if (trimmed === savedUrl) {
      return undefined;
    }

    if (trimmed && !isValidHttpUrl(trimmed)) {
      setHcpAppointmentUrlError(t("settings_messages_invalidUrl"));
      return undefined;
    }

    setHcpAppointmentUrlError(undefined);

    const timeoutId = window.setTimeout(async () => {
      const valueToSend = trimmed === "" ? null : trimmed;

      try {
        const result = await staffService.updateHcpAppointmentUrl(valueToSend);

        if (result.status >= 200 && result.status < 300) {
          changeStaffPreferences({
            ...staffPreferences,
            hcpAppointmentUrl: valueToSend ?? undefined,
          });
          showToast(t("settings_messages_hcpUrlSaved"), "success");
        } else {
          showToast(t("settings_messages_hcpUrlFailed"), "error");
        }
      } catch {
        showToast(t("settings_messages_hcpUrlFailed"), "error");
      }
    }, SAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    hcpAppointmentUrlInput,
    staffPreferences,
    changeStaffPreferences,
    showToast,
  ]);

  const handleCheckboxChange = async (isChecked: boolean) => {
    const newStafPreferences: IStaffPreferences = {
      ...staffPreferences,
      notifyByEmailOnMessage: isChecked,
    };
    changeStaffPreferences(newStafPreferences);

    const result = await staffService.updateNotifyByEmailOnMessageFlag(
      isChecked
    );

    if (result.status >= 200 && result.status < 300) {
      showToast(t("settings_messages_saved"), "success");
    } else {
      const oldStafPreferences: IStaffPreferences = {
        ...staffPreferences,
        notifyByEmailOnMessage: !isChecked,
      };
      changeStaffPreferences(oldStafPreferences);

      showToast(t("settings_messages_saveFailed"), "error");
    }
  };

  const handleAppointmentUrlChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setAppointmentUrlInput(event.target.value);
  };

  const handleHcpAppointmentUrlChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setHcpAppointmentUrlInput(event.target.value);
  };

  return (
    <ParentContainer>
      <PageTitle>{t("settings_messages_title")}</PageTitle>

      <MessagesPreferencesContainer>
        <NotificationRow>
          <TextContainer>
            {t("settings_messages_emailNotification")}
          </TextContainer>
          <Checkbox
            spacing="40px"
            label=""
            onChange={(isChecked) => {
              handleCheckboxChange(isChecked);
            }}
            checked={staffPreferences.notifyByEmailOnMessage}
          />
        </NotificationRow>

        <AppointmentUrlRow>
          <UrlLabel>{t("settings_messages_defaultUrl")}</UrlLabel>
          <AppointmentUrlField>
            <TextField
              name="defaultAppointmentUrl"
              type="text"
              label=""
              placeholder={t("settings_messages_urlPlaceholder")}
              value={appointmentUrlInput}
              onChange={handleAppointmentUrlChange}
              maxLength={MAX_URL_LENGTH}
              errorMessage={appointmentUrlError}
            />
          </AppointmentUrlField>
        </AppointmentUrlRow>

        <AppointmentUrlRow>
          <UrlLabel>{t("settings_messages_hcpUrl")}</UrlLabel>
          <AppointmentUrlField>
            <TextField
              name="hcpAppointmentUrl"
              type="text"
              label=""
              placeholder={t("settings_messages_urlPlaceholder")}
              value={hcpAppointmentUrlInput}
              onChange={handleHcpAppointmentUrlChange}
              maxLength={MAX_URL_LENGTH}
              errorMessage={hcpAppointmentUrlError}
            />
          </AppointmentUrlField>
        </AppointmentUrlRow>
      </MessagesPreferencesContainer>
    </ParentContainer>
  );
}
